import React, { useCallback, useEffect, useMemo, useState } from "react";
import GlobalQuickJump from "../../../global/GlobalQuickJump";
import { fetchUserBuilderMasters } from "../api/userBuilderApi";
import { UserSectionProvider } from "../components/UserSectionContext";
import { RoleSectionProvider } from "../components/RoleSectionContext";
import UserCreationSection from "../components/sections/UserCreationSection";
import UserDirectorySection from "../components/sections/UserDirectorySection";
import RoleCreationSection from "../components/sections/RoleCreationSection";
import RoleDirectorySection from "../components/sections/RoleDirectorySection";
import RoleModulePermissionsSection from "../components/sections/RoleModulePermissionsSection";
import RoleSubmodulePermissionsSection from "../components/sections/RoleSubmodulePermissionsSection";
import {
  DEFAULT_USER_BUILDER_SECTION,
  USER_BUILDER_SECTIONS,
} from "../config/userBuilderSections";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import "../styles/UserBuilderPage.css";
import "../styles/userBuilder.css";

const QUICK_JUMP_ITEMS = USER_BUILDER_SECTIONS.map((s) => ({
  key: s.key,
  label: s.label,
  group: s.group,
  targetId: s.targetId,
}));

export default function UserBuilderPage() {
  const [masters, setMasters] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [banner, setBanner] = useState("");
  const [successFlash, setSuccessFlash] = useState("");
  const [rolesRefreshKey, setRolesRefreshKey] = useState(0);
  const [activeSection, setActiveSection] = useState(DEFAULT_USER_BUILDER_SECTION);

  const activeMeta = useMemo(
    () => USER_BUILDER_SECTIONS.find((s) => s.key === activeSection),
    [activeSection],
  );

  const refreshMasters = useCallback(async () => {
    const mData = await fetchUserBuilderMasters();
    setMasters(mData);
    setRolesRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadError("");
      try {
        const mData = await fetchUserBuilderMasters();
        if (!cancelled) setMasters(mData);
      } catch (e) {
        if (!cancelled) setLoadError(e.message || "Could not load data.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onNotify = useCallback((msg, opts) => {
    if (!msg) {
      setBanner("");
      setSuccessFlash("");
      return;
    }
    if (opts?.variant === "error") {
      setBanner(msg);
      return;
    }
    setSuccessFlash(msg);
  }, []);

  const roles = masters?.roles || [];
  const statuses = masters?.statuses || [];
  const attendanceShifts = masters?.attendance_shifts || [];
  const attendanceLocations = masters?.attendance_locations || [];
  const showBanner =
    banner &&
    (!loadError || banner.trim().toLowerCase() !== loadError.trim().toLowerCase());

  const handleRolesChanged = useCallback(async () => {
    try {
      await refreshMasters();
    } catch (e) {
      onNotify(e.message || "Could not refresh roles.", { variant: "error" });
    }
  }, [refreshMasters, onNotify]);

  const engageUserCreateSection = useCallback(() => {
    setActiveSection("users-create");
  }, []);

  const engageRoleCreateSection = useCallback(() => {
    setActiveSection("roles-create");
  }, []);

  return (
    <div className="user-builder-shell">
      {loadError ? (
        <div className="user-builder-alert user-builder-alert--error" role="alert">
          {loadError}
        </div>
      ) : null}
      {showBanner ? (
        <div className="user-builder-banner user-builder-banner--error" role="alert">
          {banner}
        </div>
      ) : null}
      <GlobalSuccessFlashModal
        message={successFlash}
        onDismiss={() => setSuccessFlash("")}
      />

      {!masters ? (
        <p className="user-builder-loading">Loading…</p>
      ) : (
        <>
          <header className="user-builder-hero">
            <div className="user-builder-hero__text">
              <p className="user-builder-hero__eyebrow">User management</p>
              <h2 className="user-builder-hero__title">User builder</h2>
              <p className="user-builder-hero__sub">
                Use quick jump to switch workspaces. Only the selected section is
                shown — permissions, users, and roles stay focused.
              </p>
            </div>
          </header>

          <UserSectionProvider
            roles={roles}
            statuses={statuses}
            attendanceShifts={attendanceShifts}
            attendanceLocations={attendanceLocations}
            onUsersChanged={() => {}}
            onNotify={onNotify}
            onEngageSection={engageUserCreateSection}
          >
            <div className="user-builder-layout">
              <div className="user-builder-layout__main" id="ub-panel-focus">
                {activeMeta ? (
                  <p className="user-builder-workspace__hint">{activeMeta.description}</p>
                ) : null}

                {activeSection === "users-create" ? (
                  <div className="user-builder-panel user-builder-section-panel">
                    <UserCreationSection />
                  </div>
                ) : null}

                {activeSection === "users-directory" ? (
                  <div className="user-builder-panel user-builder-section-panel">
                    <div className="user-builder-panel__head">
                      <h3 className="user-builder-panel__title">User directory</h3>
                      <p className="user-builder-panel__hint">
                        Search by user code, filter columns, export, and paginate
                        through users.
                      </p>
                    </div>
                    <UserDirectorySection />
                  </div>
                ) : null}

                {activeSection === "roles-create" || activeSection === "roles-directory" ? (
                  <RoleSectionProvider
                    onRolesChanged={handleRolesChanged}
                    onNotify={onNotify}
                    onEngageSection={engageRoleCreateSection}
                  >
                    {activeSection === "roles-create" ? (
                      <div className="user-builder-panel user-builder-section-panel">
                        <RoleCreationSection />
                      </div>
                    ) : null}
                    {activeSection === "roles-directory" ? (
                      <div className="user-builder-panel user-builder-section-panel">
                        <div className="user-builder-panel__head">
                          <h3 className="user-builder-panel__title">Role directory</h3>
                          <p className="user-builder-panel__hint">
                            Active roles only. Removing a role sets it inactive (soft
                            delete).
                          </p>
                        </div>
                        <RoleDirectorySection />
                      </div>
                    ) : null}
                  </RoleSectionProvider>
                ) : null}

                {activeSection === "perm-modules" || activeSection === "perm-submodules" ? (
                  <div id="ub-permissions" className="user-builder-section-panel">
                    {activeSection === "perm-modules" ? (
                      <RoleModulePermissionsSection
                        key={`mod-${rolesRefreshKey}`}
                        onNotify={onNotify}
                      />
                    ) : null}
                    {activeSection === "perm-submodules" ? (
                      <RoleSubmodulePermissionsSection
                        key={`sub-${rolesRefreshKey}`}
                        onNotify={onNotify}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>

              <GlobalQuickJump
                title="Quick jump"
                asideAriaLabel="User builder sections"
                items={QUICK_JUMP_ITEMS}
                activeKey={activeSection}
                onActiveChange={setActiveSection}
                scrollToTarget={false}
              />
            </div>
          </UserSectionProvider>
        </>
      )}
    </div>
  );
}
